import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { dashboardAPI } from '../services/dashboardService';

export interface DailyEntry {
  id: string | number;
  project_id: number;
  project_name: string;
  hours_worked: number;
  entry_date: string;
  description?: string;
  created_at?: string;
}

export interface WeeklyDay {
  date: string; // YYYY-MM-DD
  day: string;
  entries: DailyEntry[];
  totalHours: number;
  goalHours: number;
}

export interface DashboardContextType {
  weeklyData: WeeklyDay[];
  todayEntries: DailyEntry[];
  totalHoursThisWeek: number;
  loadingDashboard: boolean;
  errorDashboard: string | null;
  refreshDashboard: () => Promise<void>;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const DashboardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [weeklyData, setWeeklyData] = useState<WeeklyDay[]>([]);
  const [todayEntries, setTodayEntries] = useState<DailyEntry[]>([]);
  const [totalHoursThisWeek, setTotalHoursThisWeek] = useState(0);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [errorDashboard, setErrorDashboard] = useState<string | null>(null);

  const refreshDashboard = useCallback(async () => {
    if (!user) return;
    setLoadingDashboard(true);
    setErrorDashboard(null);
    try {
      // Fetch weekly summary (includes all daily data)
      const weekly = await dashboardAPI.getWeeklySummary(user.id);
      setWeeklyData(weekly);

      // Get today's entries from weekly data
      const today = new Date().toISOString().split('T')[0];
      const todayData = weekly.find(day => day.date === today);
      setTodayEntries(todayData?.entries || []);

      // Calculate total hours this week
      const totalHours = weekly.reduce((sum, day) => sum + day.totalHours, 0);
      setTotalHoursThisWeek(totalHours);
    } catch (err) {
      setErrorDashboard(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoadingDashboard(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      refreshDashboard();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(
    () => ({
      weeklyData,
      todayEntries,
      totalHoursThisWeek,
      loadingDashboard,
      errorDashboard,
      refreshDashboard,
    }),
    [weeklyData, todayEntries, totalHoursThisWeek, loadingDashboard, errorDashboard, refreshDashboard]
  );

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }
  return context;
};
